import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORT_CONFIG = {
  soccer: { hasCombinedScore: false },
  rugby: { hasCombinedScore: false },
  gaelic_football: { hasCombinedScore: true },
  hurling: { hasCombinedScore: true }
};

const Overlay = () => {
  const { gameId } = useParams();
  const [game, setGame] = useState(null);
  const [localTime, setLocalTime] = useState(0);
  const [activeEvent, setActiveEvent] = useState(null);
  const [showStoppage, setShowStoppage] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const timerRef = useRef(null);
  const pollRef = useRef(null);

  // Fetch game data
  const fetchGame = async () => {
    try {
      const response = await axios.get(`${API}/overlay/${gameId}`);
      const data = response.data;
      setGame(data);
      setLocalTime(data.current_time);
      setActiveEvent(data.active_event);
      setLoaded(true);
      return data;
    } catch (error) {
      console.error('Failed to load game');
      setLoaded(true);
      return null;
    }
  };

  useEffect(() => {
    fetchGame();
    pollRef.current = setInterval(fetchGame, 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [gameId]);

  // Timer logic
  useEffect(() => {
    if (game?.is_running) {
      timerRef.current = setInterval(() => {
        setLocalTime(prev => {
          const newTime = game.timer_direction === 'up' ? prev + 1 : Math.max(0, prev - 1);
          return newTime;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [game?.is_running, game?.timer_direction]);

  // Show stoppage at 45+ or 90+
  useEffect(() => {
    if (game?.stoppage_time > 0) {
      const mins = Math.floor(localTime / 60);
      if (mins >= 45) setShowStoppage(true);
    } else {
      setShowStoppage(false);
    }
  }, [game?.stoppage_time, localTime]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatGAAScore = (team, sport) => {
    if (SPORT_CONFIG[sport]?.hasCombinedScore) {
      return `${team.goals || 0}-${team.points || 0}`;
    }
    return team.score || 0;
  };

  // Colors from SVG
  const DARK_BLUE = '#234968';
  const LIGHT_BLUE = '#CCEAFF';
  const WHITE = '#FFFFFF';

  // If not loaded yet, show nothing (transparent)
  if (!loaded || !game) {
    return (
      <>
        <style>{`
          html, body, #root {
            margin: 0 !important;
            padding: 0 !important;
            background: transparent !important;
            background-color: transparent !important;
            overflow: hidden !important;
          }
        `}</style>
        <div style={{ width: '1920px', height: '1080px', background: 'transparent' }} />
      </>
    );
  }

  const sportConfig = SPORT_CONFIG[game.sport];
  const period = game.period || 'pregame';

  // Period overlay
  const getPeriodOverlay = () => {
    switch(period) {
      case 'halftime': return 'HT';
      case 'halftime_et': return 'HT';
      case 'fulltime': return 'FT';
      case 'pregame': return null;
      default: return null;
    }
  };

  const periodOverlayText = getPeriodOverlay();
  const showScoreboard = !periodOverlayText && period !== 'pregame';
  const hasCards = game.team1.yellow_cards > 0 || game.team1.red_cards > 0 || 
                   game.team2.yellow_cards > 0 || game.team2.red_cards > 0;

  return (
    <>
      <style>{`
        html, body, #root {
          margin: 0 !important;
          padding: 0 !important;
          background: transparent !important;
          background-color: transparent !important;
          overflow: hidden !important;
        }
        @keyframes goalFlash {
          0%, 100% { opacity: 0; }
          10%, 90% { opacity: 1; }
        }
        @keyframes bounceIn {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      
      <div style={{
        width: '1920px',
        height: '1080px',
        position: 'relative',
        overflow: 'hidden',
        background: 'transparent',
        fontFamily: "'Open Sans', Arial, sans-serif",
      }}>
        
        {/* Event Animation (GOAL/TRY/POINT) */}
        {activeEvent && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 50,
            animation: 'goalFlash 3s ease-in-out forwards',
          }}>
            <div style={{ animation: 'bounceIn 0.5s ease-out' }}>
              <div style={{
                fontFamily: "'Open Sans', Arial, sans-serif",
                fontWeight: 700,
                fontSize: '200px',
                color: activeEvent === 'GOAL' ? '#22C55E' : activeEvent === 'TRY' ? '#3B82F6' : '#F97316',
                textShadow: '4px 4px 0px rgba(0,0,0,0.8)',
              }}>
                {activeEvent}!
              </div>
            </div>
          </div>
        )}

        {/* Period Overlay (HT/FT) */}
        {periodOverlayText && (
          <div style={{
            position: 'absolute',
            top: '54px',
            left: '97px',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              width: '543px',
              height: '24px',
              backgroundColor: DARK_BLUE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontWeight: 700,
                fontSize: '11px',
                color: WHITE,
                letterSpacing: '0.3px',
              }}>
                NEW LOGICAL SPORTS
              </span>
            </div>
            <div style={{
              width: '543px',
              height: '120px',
              backgroundColor: LIGHT_BLUE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontWeight: 700,
                fontSize: '72px',
                color: '#000000',
              }}>
                {periodOverlayText}
              </span>
            </div>
          </div>
        )}

        {/* Main Scoreboard */}
        {showScoreboard && (
          <div style={{
            position: 'absolute',
            top: '54px',
            left: '97px',
          }}>
            {/* Brand Tag */}
            <div style={{
              position: 'absolute',
              left: '193px',
              top: '0px',
              width: '156px',
              height: '24px',
              backgroundColor: DARK_BLUE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontWeight: 700,
                fontSize: '11px',
                color: WHITE,
                letterSpacing: '0.3px',
                whiteSpace: 'nowrap',
              }}>
                NEW LOGICAL SPORTS
              </span>
            </div>

            {/* Competition Bar */}
            <div style={{
              position: 'absolute',
              left: '42px',
              top: '24px',
              width: '459px',
              height: '31px',
              backgroundColor: WHITE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontWeight: 600,
                fontSize: '23px',
                color: DARK_BLUE,
              }}>
                {game.competition_name || 'MATCH'}
              </span>
            </div>

            {/* Main Scoreboard Bar */}
            <div style={{
              position: 'absolute',
              left: '0px',
              top: '56px',
              width: '543px',
              height: '50px',
              backgroundColor: DARK_BLUE,
              display: 'flex',
              alignItems: 'center',
            }}>
              {/* Team 1 */}
              <div style={{
                width: '195px',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <span style={{
                  fontWeight: 700,
                  fontSize: '22px',
                  color: WHITE,
                  textTransform: 'uppercase',
                }}>
                  {game.team1.name}
                </span>
              </div>

              {/* Score Box */}
              <div style={{
                width: '154px',
                height: '50px',
                backgroundColor: LIGHT_BLUE,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <span style={{
                  fontWeight: 700,
                  fontSize: '42px',
                  color: '#000000',
                }}>
                  {formatGAAScore(game.team1, game.sport)} - {formatGAAScore(game.team2, game.sport)}
                </span>
              </div>

              {/* Team 2 */}
              <div style={{
                width: '195px',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <span style={{
                  fontWeight: 700,
                  fontSize: '22px',
                  color: WHITE,
                  textTransform: 'uppercase',
                }}>
                  {game.team2.name}
                </span>
              </div>
            </div>

            {/* Timer Box */}
            <div style={{
              position: 'absolute',
              left: '195px',
              top: '106px',
              width: '154px',
              height: '50px',
              backgroundColor: WHITE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontWeight: 600,
                fontSize: '40px',
                color: DARK_BLUE,
              }}>
                {formatTime(localTime)}
              </span>
            </div>

            {/* Stoppage Time */}
            {showStoppage && game.stoppage_time > 0 && (
              <div style={{
                position: 'absolute',
                left: '352px',
                top: '106px',
                height: '50px',
                backgroundColor: '#F97316',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 16px',
                animation: 'fadeIn 0.5s ease-in-out',
              }}>
                <span style={{
                  fontWeight: 700,
                  fontSize: '32px',
                  color: WHITE,
                }}>
                  +{game.stoppage_time}'
                </span>
              </div>
            )}

            {/* Cards - Below timer */}
            {hasCards && (
              <div style={{
                position: 'absolute',
                left: '195px',
                top: '158px',
                width: '154px',
                display: 'flex',
                justifyContent: 'center',
                gap: '12px',
                backgroundColor: 'rgba(35, 73, 104, 0.9)',
                padding: '6px 12px',
                borderRadius: '0 0 4px 4px',
              }}>
                {/* Team 1 Cards */}
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {game.team1.yellow_cards > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <div style={{ width: '12px', height: '16px', backgroundColor: '#EAB308', borderRadius: '1px' }} />
                      <span style={{ color: WHITE, fontSize: '12px', fontWeight: 700 }}>
                        {game.team1.yellow_cards}
                      </span>
                    </div>
                  )}
                  {game.team1.red_cards > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <div style={{ width: '12px', height: '16px', backgroundColor: '#EF4444', borderRadius: '1px' }} />
                      <span style={{ color: WHITE, fontSize: '12px', fontWeight: 700 }}>
                        {game.team1.red_cards}
                      </span>
                    </div>
                  )}
                </div>
                <div style={{ width: '1px', backgroundColor: 'rgba(255,255,255,0.3)', height: '20px' }} />
                {/* Team 2 Cards */}
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {game.team2.yellow_cards > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <div style={{ width: '12px', height: '16px', backgroundColor: '#EAB308', borderRadius: '1px' }} />
                      <span style={{ color: WHITE, fontSize: '12px', fontWeight: 700 }}>
                        {game.team2.yellow_cards}
                      </span>
                    </div>
                  )}
                  {game.team2.red_cards > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <div style={{ width: '12px', height: '16px', backgroundColor: '#EF4444', borderRadius: '1px' }} />
                      <span style={{ color: WHITE, fontSize: '12px', fontWeight: 700 }}>
                        {game.team2.red_cards}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* GAA Combined Score */}
            {sportConfig?.hasCombinedScore && (
              <div style={{
                position: 'absolute',
                left: '0px',
                top: hasCards ? '195px' : '160px',
                width: '543px',
                display: 'flex',
                justifyContent: 'space-between',
                padding: '4px 20px',
                boxSizing: 'border-box',
              }}>
                <span style={{
                  backgroundColor: DARK_BLUE,
                  color: WHITE,
                  padding: '3px 10px',
                  fontSize: '14px',
                  fontWeight: 600,
                }}>
                  ({game.team1.score})
                </span>
                <span style={{
                  backgroundColor: DARK_BLUE,
                  color: WHITE,
                  padding: '3px 10px',
                  fontSize: '14px',
                  fontWeight: 600,
                }}>
                  ({game.team2.score})
                </span>
              </div>
            )}

            {/* Player Notices */}
            {(game.team1.player_notice || game.team2.player_notice) && (
              <div style={{ 
                position: 'absolute',
                left: '0px',
                top: hasCards ? '220px' : '185px',
                width: '543px',
              }}>
                {game.team1.player_notice && (
                  <div style={{
                    backgroundColor: 'rgba(239, 68, 68, 0.95)',
                    color: WHITE,
                    padding: '4px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    textAlign: 'center',
                    marginBottom: '2px',
                  }}>
                    {game.team1.name}: {game.team1.player_notice}
                  </div>
                )}
                {game.team2.player_notice && (
                  <div style={{
                    backgroundColor: 'rgba(239, 68, 68, 0.95)',
                    color: WHITE,
                    padding: '4px 12px',
                    fontSize: '12px',
                    fontWeight: 600,
                    textAlign: 'center',
                  }}>
                    {game.team2.name}: {game.team2.player_notice}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Overlay;
